import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

export const WealthOwnerDetailsFormContext = createContext({ })

export const useWealthOwnerDetails = () => {
  return useContext(WealthOwnerDetailsFormContext)
}

export const useSectionEditing = (sectionName) => {
  const { editing, section } = useWealthOwnerDetails()
  return useMemo(() => {
    return editing && section === sectionName
  }, [editing, section, sectionName])
}

const useWealthOwnerDetailsValue = ({ wealthOwner, refetch, deleteWealthOwner, defaultState }) => {
  const [formState, setFormState] = useState(defaultState)

  const editSection = useCallback(({
    section,
    saveDescription = 'Save',
    onSave
  }) => {
    setFormState((prevState) => ({
      ...prevState,
      editing: !prevState.editing,
      section: !prevState.editing ? section : null,
      saveDescription,
      onSave
    }))
  }, [setFormState])

  const cancelEdit = useCallback(() => {
    setFormState((prevState) => ({
      ...prevState,
      editing: false,
      section: null,
      saveDescription: null,
      onSave: null
    }))
  }, [setFormState])

  return useMemo(() => ({
    wealthOwner,
    refetchWealthOwner: refetch,
    deleteWealthOwner,
    editSection,
    cancelEdit,
    ...formState
  }), [wealthOwner, editSection, cancelEdit, formState, refetch, deleteWealthOwner])
}

export const WealthOwnerDetailsProvider = ({ wealthOwner, refetch, deleteWealthOwner, children, defaultState }) => {
  const value = useWealthOwnerDetailsValue({
    wealthOwner,
    refetch,
    defaultState,
    deleteWealthOwner
  })

  return (
    <WealthOwnerDetailsFormContext.Provider value={value}>
      {children}
    </WealthOwnerDetailsFormContext.Provider>
  )
}

WealthOwnerDetailsProvider.propTypes = {
  defaultState: PropTypes.shape({
    editing: PropTypes.bool,
    section: PropTypes.string,
    saveDescription: PropTypes.string,
    onSave: PropTypes.func
  }),
  wealthOwner: PropTypes.object,
  children: PropTypes.node,
  refetch: PropTypes.func,
  deleteWealthOwner: PropTypes.func
}

WealthOwnerDetailsProvider.defaultProps = {
  defaultState: {
    editing: false,
    section: null,
    saveDescription: null,
    onSave: null
  }
}
